import { useEffect, useRef, useState } from "react";
import { Play, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Fase E3 — configura como o Nexo te chama + toggle da saudação + chimes.
 * Backend: PATCH /api/preferences aceita address_form, greeting_enabled,
 * chimes_enabled. GET /api/voice/greeting devolve o áudio cacheado.
 *
 * Pós SEC-1: /api/preferences exige JWT via Authorization Bearer.
 * authFetch injeta o token automaticamente.
 */
export default function GreetingSettings({ userId, authToken }) {
  const [address, setAddress] = useState("");
  const [initialAddress, setInitialAddress] = useState("");
  const [greetingEnabled, setGreetingEnabled] = useState(true);
  const [chimesEnabled, setChimesEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${API}/preferences`);
        if (cancelled) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setAddress(data?.address_form || "Mestre");
        setInitialAddress(data?.address_form || "Mestre");
        setGreetingEnabled(data?.greeting_enabled !== false);
        setChimesEnabled(data?.chimes_enabled !== false);
      } catch (e) {
        console.warn("[GreetingSettings] failed to load preferences", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} audioRef.current = null; }
  }, []);

  const persist = async (partial) => {
    try {
      const r = await authFetch(`${API}/preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(partial),
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(typeof detail?.detail === "string" ? detail.detail : `HTTP ${r.status}`);
      }
      return true;
    } catch (e) {
      console.error("[GreetingSettings] persist failed", e);
      toast.error(e.message || "Falha ao salvar");
      return false;
    }
  };

  const handleAddressSave = async () => {
    const v = address.trim();
    if (v.length < 2 || v.length > 30) {
      toast.error("Use entre 2 e 30 caracteres");
      return;
    }
    setSaving(true);
    const ok = await persist({ address_form: v });
    if (ok) {
      setInitialAddress(v);
      toast.success(`Agora te chamo de ${v}`);
    }
    setSaving(false);
  };

  const handleToggle = async (field, value) => {
    const setter = field === "greeting_enabled" ? setGreetingEnabled : setChimesEnabled;
    const prev = field === "greeting_enabled" ? greetingEnabled : chimesEnabled;
    setter(value); // otimista
    const ok = await persist({ [field]: value });
    if (!ok) setter(prev);
  };

  const handlePreviewGreeting = async () => {
    if (previewing) return;
    setPreviewing(true);
    try {
      // Salva primeiro se mudou, pra preview usar o nome novo.
      if (address.trim() !== initialAddress) {
        const ok = await persist({ address_form: address.trim() });
        if (ok) setInitialAddress(address.trim());
      }
      // Busca via fetch pra poder mandar Authorization header (necessário pra
      // greeting usar as prefs do user). axios tbm funcionaria.
      const r = await fetch(`${API}/voice/greeting?ts=${Date.now()}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (r.status === 204) {
        toast("Saudação desligada");
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      if (!blob || blob.size === 0) throw new Error("áudio vazio");
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); audioRef.current = null; setPreviewing(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); audioRef.current = null; setPreviewing(false); toast.error("Falha ao tocar"); };
      await audio.play();
    } catch (e) {
      console.error("[GreetingSettings] preview failed", e);
      toast.error("Falha ao gerar amostra");
      setPreviewing(false);
    }
  };

  const dirty = address.trim() !== initialAddress;

  return (
    <div className="space-y-6" data-testid="greeting-settings">
      <section className="space-y-3">
        <div>
          <label className="block text-caption text-foreground-muted uppercase tracking-wider mb-2">
            Como você quer ser chamado
          </label>
          <div className="flex gap-2">
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Mestre"
              disabled={loading}
              maxLength={30}
              className="flex-1"
              data-testid="address-form-input"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={handleAddressSave}
              disabled={!dirty || saving}
              data-testid="address-form-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </Button>
          </div>
          <p className="text-caption text-foreground-subtle mt-2">
            O Nexo te recebe dizendo <span className="text-foreground">"{address || "Mestre"}, em que posso ajudar?"</span>.
            Você também pode dizer <span className="italic">"me chama de X"</span> a qualquer momento.
          </p>
        </div>

        <button
          type="button"
          onClick={handlePreviewGreeting}
          disabled={previewing}
          className={cn(
            "inline-flex items-center gap-2 text-caption px-3 py-1.5 rounded-md border transition-colors duration-fast ease-smooth",
            previewing
              ? "bg-brand-cyan/10 border-brand-cyan/30 text-brand-cyan"
              : "bg-surface-card-hover border-subtle text-foreground-muted hover:text-foreground hover:border-strong",
          )}
          data-testid="greeting-preview"
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Ouvir saudação
        </button>
      </section>

      <section className="space-y-3 border-t border-subtle pt-6">
        <ToggleRow
          label="Saudação ao ativar"
          description="Nexo te cumprimenta antes de começar a escutar."
          value={greetingEnabled}
          onChange={(v) => handleToggle("greeting_enabled", v)}
          testId="toggle-greeting"
        />
        <ToggleRow
          label="Sons de ativação"
          description="Chime sutil ao começar e terminar de ouvir (estilo Alexa)."
          value={chimesEnabled}
          onChange={(v) => handleToggle("chimes_enabled", v)}
          testId="toggle-chimes"
        />
      </section>
    </div>
  );
}

function ToggleRow({ label, description, value, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0">
        <div className="text-body text-foreground">{label}</div>
        <p className="text-caption text-foreground-muted mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={cn(
          "relative w-11 h-6 rounded-full transition-colors duration-fast ease-smooth shrink-0",
          value ? "bg-brand-cyan" : "bg-surface-card-hover border border-subtle",
        )}
        data-testid={testId}
      >
        <span
          className={cn(
            "absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-fast ease-smooth",
            value ? "translate-x-[22px]" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
